import type { AnalyticsCohortRow, AnalyticsView } from '@trendator/shared';
import { Link } from '../components/Layout';
import { Card, CardTitle, Empty, ErrorBox, Label, Loading, PageHead, Segmented, Table } from '../components/ui';
import { cn } from '../lib/cn';
import { num, rub } from '../lib/format';
import { useLoad } from '../lib/hooks';
import { api } from '../lib/api';
import { setQuery } from '../lib/router';

const PERIODS = [
  { value: '7', label: '7 дней' },
  { value: '30', label: '30 дней' },
  { value: '90', label: '90 дней' },
] as const;
const BY = [
  { value: 'source', label: 'Источник' },
  { value: 'sub', label: 'Партнёр · саб' },
] as const;

const SOURCE_LABEL: Record<string, string> = { bot: 'Бот (/start)', miniapp: 'Мини-апп напрямую', ref: 'Партнёрка', duo: 'Дуо (позвал друг)' };

const pct = (a: number, b: number) => (b > 0 ? `${(Math.round((a / b) * 1000) / 10).toLocaleString('ru-RU')}%` : '—');
const SCREEN_LABEL: Record<string, string> = {
  main: 'Главная',
  history: 'История',
  twin: 'Двойник',
  profile: 'Профиль',
  create: 'Создать',
  generation: 'Результат',
  topup: 'Пополнение',
  payment: 'Оплата',
  catalog: 'Каталог',
  trend: 'Тренд',
  twinSetup: 'Настройка двойника',
  withdraw: 'Вывод',
  duo: 'Дуо',
};

export function Analytics({ query }: { query: URLSearchParams }) {
  const period = (PERIODS.find((p) => p.value === query.get('period'))?.value ?? '30') as (typeof PERIODS)[number]['value'];
  const by = (query.get('by') === 'sub' ? 'sub' : 'source') as 'source' | 'sub';
  const d = useLoad(() => api.get<AnalyticsView>(`/analytics?period=${period}&by=${by}`), [period, by]);

  if (d.error && !d.data) return <ErrorBox text={d.error} onRetry={() => void d.reload()} />;
  const v = d.data;
  const totals = v?.days.reduce((a, x) => ({ revenue: a.revenue + x.revenue, cost: a.cost + x.costRub, margin: a.margin + x.margin, gens: a.gens + x.generations, users: a.users + x.newUsers }), {
    revenue: 0,
    cost: 0,
    margin: 0,
    gens: 0,
    users: 0,
  });

  return (
    <>
      <PageHead title="Аналитика" actions={<Segmented value={period} onChange={(p) => setQuery({ period: p === '30' ? null : p })} options={PERIODS} />} />
      {!v ? <Loading /> : null}
      {v && totals ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label="Выручка" value={rub(totals.revenue)} />
            <Kpi label="Себестоимость" value={rub(totals.cost)} />
            <Kpi label="Маржа" value={rub(totals.margin)} sub={pct(totals.margin, totals.revenue)} />
            <Kpi label="Генерации" value={num(totals.gens)} />
            <Kpi label="Новые юзеры" value={num(totals.users)} />
          </div>

          <Card className="flex flex-col gap-3 px-2 pt-3 pb-1">
            <div className="flex flex-wrap items-center justify-between gap-2 px-2">
              <div className="flex flex-col gap-1">
                <span className="text-base font-bold">Воронка по источникам</span>
                <Label>юзеры, пришедшие за период: старт → согласие → генерация → оплата → повторная</Label>
              </div>
              <Segmented value={by} onChange={(b) => setQuery({ by: b === 'source' ? null : b })} options={BY} />
            </div>
            {v.funnel.length === 0 ? <Empty>Новых юзеров за период нет</Empty> : null}
            {v.funnel.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>{by === 'sub' ? 'Партнёр · саб' : 'Источник'}</th>
                    <th className="text-right!">Старт</th>
                    <th className="text-right!">Согласие</th>
                    <th className="text-right!">Генерация</th>
                    <th className="text-right!">Оплата</th>
                    <th className="text-right!">Повторная</th>
                    <th className="text-right!">Выручка</th>
                    <th className="text-right!">₽ на юзера</th>
                  </tr>
                </thead>
                <tbody>
                  {v.funnel.map((r) => (
                    <tr key={r.key}>
                      <td className="font-semibold">{by === 'source' ? (SOURCE_LABEL[r.key] ?? r.key) : r.key}</td>
                      <td className="text-right">{num(r.started)}</td>
                      <Cell n={r.consent} of={r.started} />
                      <Cell n={r.generated} of={r.started} />
                      <Cell n={r.paid} of={r.started} strong />
                      <Cell n={r.repeat} of={r.paid} />
                      <td className="text-right font-semibold">{rub(r.revenue)}</td>
                      <td className="text-right text-muted">{r.started ? rub(Math.round(r.revenue / r.started)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </Card>

          <div className="flex flex-wrap gap-4">
            <Card className="flex min-w-0 flex-[1_1_420px] flex-col gap-3 px-2 pt-3 pb-1">
              <CardTitle className="px-2" right={<Label>вернулись на N-й день и позже</Label>}>
                Когорты по неделям
              </CardTitle>
              {v.cohorts.length === 0 ? <Empty>Пока нет юзеров</Empty> : <Cohorts rows={v.cohorts} />}
            </Card>

            <Card className="flex min-w-0 flex-[1_1_320px] flex-col gap-3 px-2 pt-3 pb-1">
              <CardTitle className="px-2" right={<Label>мини-апп</Label>}>
                Экраны
              </CardTitle>
              {v.screens.length === 0 ? <Empty>Событий мини-аппа пока нет</Empty> : null}
              {v.screens.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Экран</th>
                      <th className="text-right!">Показов</th>
                      <th className="text-right!">Юзеров</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.screens.map((s) => (
                      <tr key={s.name}>
                        <td>{SCREEN_LABEL[s.name] ?? s.name}</td>
                        <td className="text-right">{num(s.views)}</td>
                        <td className="text-right text-muted">{num(s.users)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : null}
            </Card>
          </div>

          <Card className="flex flex-col gap-3 px-2 pt-3 pb-1">
            <CardTitle className="px-2" right={<Label>просмотр карточки → запуск</Label>}>
              Конверсия трендов
            </CardTitle>
            {v.trends.length === 0 ? <Empty>За период трендов не открывали</Empty> : null}
            {v.trends.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>Тренд</th>
                    <th className="text-right!">Просмотры</th>
                    <th className="text-right!">Запуски</th>
                    <th className="text-right!">Конверсия</th>
                    <th className="text-right!">Выручка</th>
                  </tr>
                </thead>
                <tbody>
                  {v.trends.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Link to={`/trends/${t.id}`} className="font-semibold text-white">
                          {t.title}
                        </Link>
                      </td>
                      <td className="text-right">{num(t.views)}</td>
                      <td className="text-right">{num(t.generations)}</td>
                      <td className={cn('text-right font-semibold', t.views >= 20 && t.conversion < 5 ? 'text-warn' : '')}>{t.views ? `${t.conversion.toLocaleString('ru-RU')}%` : '—'}</td>
                      <td className="text-right">{rub(t.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </Card>

          <Card className="flex flex-col gap-3 px-2 pt-3 pb-1">
            <CardTitle className="px-2" right={<Label>по Москве · маржа за вычетом эквайринга</Label>}>
              По дням
            </CardTitle>
            <Table>
              <thead>
                <tr>
                  <th>День</th>
                  <th className="text-right!">Выручка</th>
                  <th className="text-right!">Себестоимость</th>
                  <th className="text-right!">Маржа</th>
                  <th className="text-right!">Генерации</th>
                  <th className="text-right!">Новые</th>
                </tr>
              </thead>
              <tbody>
                {[...v.days].reverse().map((x) => (
                  <tr key={x.day}>
                    <td className="text-soft">{new Date(`${x.day}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' })}</td>
                    <td className="text-right font-semibold">{rub(x.revenue)}</td>
                    <td className="text-right text-muted">{rub(x.costRub)}</td>
                    <td className={cn('text-right font-semibold', x.margin < 0 ? 'text-bad' : x.margin > 0 ? 'text-ok' : '')}>{rub(x.margin)}</td>
                    <td className="text-right">{num(x.generations)}</td>
                    <td className="text-right">{num(x.newUsers)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      ) : null}
    </>
  );
}

function Cell({ n, of, strong }: { n: number; of: number; strong?: boolean }) {
  return (
    <td className="text-right">
      <span className={strong ? 'font-semibold' : ''}>{num(n)}</span> <span className="text-xs text-muted">{pct(n, of)}</span>
    </td>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <b className={cn('text-[22px] leading-none font-extrabold tracking-tight', value.startsWith('-') ? 'text-bad' : '')}>{value}</b>
      {sub ? <span className="text-xs text-muted">{sub}</span> : null}
    </Card>
  );
}

/** Ячейка когорты: чем больше вернулось, тем ярче. */
function Cohorts({ rows }: { rows: AnalyticsCohortRow[] }) {
  const heat = (v: number | null) => (v === null ? undefined : { background: `rgba(45,180,255,${0.08 + Math.min(1, v / 60) * 0.5})` });
  const cell = (v: number | null) => (
    <td className="text-right" style={heat(v)}>
      {v === null ? <span className="text-dim">—</span> : `${v.toLocaleString('ru-RU')}%`}
    </td>
  );
  return (
    <Table>
      <thead>
        <tr>
          <th>Неделя</th>
          <th className="text-right!">Юзеров</th>
          <th className="text-right!">D1</th>
          <th className="text-right!">D7</th>
          <th className="text-right!">D30</th>
          <th className="text-right!">Платящих</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.week}>
            <td className="text-soft">с {new Date(`${c.week}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}</td>
            <td className="text-right">{num(c.users)}</td>
            {cell(c.d1)}
            {cell(c.d7)}
            {cell(c.d30)}
            <td className="text-right font-semibold">{c.paying.toLocaleString('ru-RU')}%</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
